import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import { ImagePreview } from 'components/Image/ImagePreview';
import { VideoPreview } from 'components/Video/VideoPreview';
import { RichMediaTemplateId } from 'core/creative/Creative';

export type RichMediaPreviewMedia =
  | { url?: string; file?: File }
  | undefined
  | null;

interface RichMediaTemplatePreviewProps {
  // 有給就直接採用；沒給時維持由 expandVideo 推導的既有行為（保護既有呼叫端）
  templateId?: string;
  barImage?: RichMediaPreviewMedia;
  expandImage?: RichMediaPreviewMedia;
  expandVideo?: RichMediaPreviewMedia;
  // 跑馬燈的三張輪播圖，順序即輪播順序
  marqueeImages?: RichMediaPreviewMedia[];
  landingPage?: string;
  className?: string;
}

export const isMarqueeTemplateId = (templateId?: string): boolean =>
  templateId === RichMediaTemplateId.MARQUEE_PC ||
  templateId === RichMediaTemplateId.MARQUEE_MOBILE;

// 桌機殼在 template 內是 box-sizing: border-box 的 .desktop-shell（780px 寬）＋ body 左右各 16px padding
// → 812px 才不會被 max-width: 100% 壓縮。手機殼與既有 EXPAND_* 維持 380 寬。
// 高度只是 iframe 載入前的暫定值：srcdoc 同源，載入後直接量 template 的實際內容高度覆蓋掉，
// 免得寫死的估算值比實際內容高，在外層留下一大塊空白可以捲。
const DESKTOP_PREVIEW_SIZE = { width: 812, height: 680 };
const MOBILE_PREVIEW_SIZE = { width: 380, height: 820 };

// 送審端提供的 Rich Media 預覽模板；讀取 query string 的 barImageUrl / expandImageUrl /
// expandVideoUrl / marqueeImageUrl1 / marqueeImageUrl2 / marqueeImageUrl3 / landingPage /
// templateId 後渲染手機或桌機模擬版位。CDN 回應
// access-control-allow-origin: *，因此可在 runtime fetch HTML 文字，改用 iframe srcdoc
// 同源渲染，讓尚未上傳的本地素材（object URL）也能在預覽中載入。
const TEMPLATE_URL =
  'https://static-cdn.pchome.tw/rich-media-template/richmedia_template.html';

// 同一份模板全站共用，快取 fetch promise 避免重複下載；失敗時清掉 promise 讓下次可重試。
let templatePromise: Promise<string> | undefined;
const fetchTemplate = (): Promise<string> => {
  if (!templatePromise) {
    templatePromise = fetch(TEMPLATE_URL)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load rich media template: ${res.status}`);
        }
        return res.text();
      })
      .catch((err) => {
        templatePromise = undefined;
        throw err;
      });
  }
  return templatePromise;
};

const buildQuery = (params: Record<string, string | undefined>): string =>
  Object.entries(params)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join('&');

// 模板以 location.search 解析參數；srcdoc 下沒有 search，故把該存取點換成我們組好的字串字面值，
// 其餘解析邏輯（decodeURIComponent 等）維持模板原樣，對 CDN 版本改動的耦合降到最低。
const injectParams = (html: string, query: string): string => {
  const searchLiteral = JSON.stringify(query ? `?${query}` : '');
  if (html.includes('location.search')) {
    return html.replace('location.search', searchLiteral);
  }
  return html;
};

export const RichMediaTemplatePreview: React.FC<RichMediaTemplatePreviewProps> = ({
  templateId: templateIdProp,
  barImage,
  expandImage,
  expandVideo,
  marqueeImages,
  landingPage,
  className
}) => {
  // 拆成基本型別當作 effect 依賴，避免呼叫端每次 render 傳新物件造成無限重跑。
  const barImageUrlProp = barImage?.url;
  const barImageFile = barImage?.file;
  const expandImageUrlProp = expandImage?.url;
  const expandImageFile = expandImage?.file;
  const expandVideoUrlProp = expandVideo?.url;
  const expandVideoFile = expandVideo?.file;
  const marqueeImage1UrlProp = marqueeImages?.[0]?.url;
  const marqueeImage1File = marqueeImages?.[0]?.file;
  const marqueeImage2UrlProp = marqueeImages?.[1]?.url;
  const marqueeImage2File = marqueeImages?.[1]?.file;
  const marqueeImage3UrlProp = marqueeImages?.[2]?.url;
  const marqueeImage3File = marqueeImages?.[2]?.file;

  const isMarquee = isMarqueeTemplateId(templateIdProp);
  const previewSize = templateIdProp === RichMediaTemplateId.MARQUEE_PC
    ? DESKTOP_PREVIEW_SIZE
    : MOBILE_PREVIEW_SIZE;
  const previewWidth = previewSize.width;
  const previewHeight = previewSize.height;

  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState<number | undefined>();
  const [srcDoc, setSrcDoc] = useState<string>('');
  const [failed, setFailed] = useState(false);

  // 版型換掉時先丟掉上一個 template 的量測值，避免用舊高度渲染新 template。
  useEffect(() => setContentHeight(undefined), [previewHeight]);

  // srcdoc 是同源，載入後直接量 template 的實際內容高度，取代寫死的估算值。
  const handleIframeLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const body = event.currentTarget.contentDocument?.body;
    if (body) {
      setContentHeight(body.scrollHeight);
    }
  }, []);

  const frameHeight = contentHeight ?? previewHeight;

  // 上稿頁版面容不下 template 需要的寬度時，等比縮放外層容器（不改 template HTML 本身）。
  // 用 ResizeObserver 而非 window resize：容器寬度也會因側邊欄收合、分頁切換等非視窗事件改變。
  // clientWidth 含 padding（呼叫端的 className 可能有 padding），故扣掉 padding 取真正的內容寬度。
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const updateScale = () => {
      const style = window.getComputedStyle(container);
      const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const available = container.clientWidth - padding;
      setScale(available > 0 && available < previewWidth ? available / previewWidth : 1);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, [previewWidth, failed]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    // 已上傳素材有真實 url；新建素材只有本地 File，轉成 object URL（srcdoc 同源，iframe 可讀）。
    const toUrl = (url?: string, file?: File): string | undefined => {
      if (file) {
        const objectUrl = URL.createObjectURL(file);
        objectUrls.push(objectUrl);
        return objectUrl;
      }
      return url || undefined;
    };

    let query: string;
    if (isMarquee) {
      // 跑馬燈只帶 templateId + marqueeImageUrl1/2/3 + landingPage
      query = buildQuery({
        templateId: templateIdProp,
        marqueeImageUrl1: toUrl(marqueeImage1UrlProp, marqueeImage1File),
        marqueeImageUrl2: toUrl(marqueeImage2UrlProp, marqueeImage2File),
        marqueeImageUrl3: toUrl(marqueeImage3UrlProp, marqueeImage3File),
        landingPage
      });
    } else {
      const barImageUrl = toUrl(barImageUrlProp, barImageFile);
      const expandVideoUrl = toUrl(expandVideoUrlProp, expandVideoFile);
      const expandImageUrl = expandVideoUrl
        ? undefined
        : toUrl(expandImageUrlProp, expandImageFile);
      const templateId = templateIdProp || (expandVideoUrl
        ? RichMediaTemplateId.EXPAND_VIDEO
        : RichMediaTemplateId.EXPAND_IMAGE);

      query = buildQuery({
        templateId,
        barImageUrl,
        expandImageUrl,
        expandVideoUrl,
        landingPage
      });
    }

    setFailed(false);
    fetchTemplate()
      .then((html) => {
        if (!cancelled) {
          setSrcDoc(injectParams(html, query));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [
    isMarquee,
    templateIdProp,
    barImageUrlProp,
    barImageFile,
    expandImageUrlProp,
    expandImageFile,
    expandVideoUrlProp,
    expandVideoFile,
    marqueeImage1UrlProp,
    marqueeImage1File,
    marqueeImage2UrlProp,
    marqueeImage2File,
    marqueeImage3UrlProp,
    marqueeImage3File,
    landingPage
  ]);

  // 模板載入失敗時退回原本的縮圖預覽，確保畫面不會空白。
  if (failed) {
    const renderImage = (media: RichMediaPreviewMedia) => {
      if (!media || (!media.url && !media.file)) {
        return null;
      }
      return (
        <ImagePreview
          url={media.url}
          file={media.file}
          border={false}
          config={{ showName: false, showSize: true }}
        />
      );
    };
    // 跑馬燈沒有置底圖／主素材，改列出三張輪播圖（順序即輪播順序）。
    if (isMarquee) {
      return (
        <div className={className}>
          {[marqueeImages?.[0], marqueeImages?.[1], marqueeImages?.[2]].map((media, index) => (
            <div key={index}>
              <div>
                {i18n.t<string>(`creativeSetupFlow.labels.richMediaMarqueeImage${index + 1}`)}
              </div>
              {renderImage(media)}
            </div>
          ))}
        </div>
      );
    }
    const hasExpandVideo = Boolean(expandVideoUrlProp || expandVideoFile);
    return (
      <div className={className}>
        <div>
          <div>{i18n.t<string>('creativeSetupFlow.labels.richMediaBarImage')}</div>
          {renderImage(barImage)}
        </div>
        {hasExpandVideo ? (
          <div>
            <div>{i18n.t<string>('creativeSetupFlow.labels.richMediaExpandVideo')}</div>
            <VideoPreview src={(expandVideoFile || expandVideoUrlProp) as File | string} />
          </div>
        ) : (
          <div>
            <div>{i18n.t<string>('creativeSetupFlow.labels.richMediaExpandImage')}</div>
            {renderImage(expandImage)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      {srcDoc && (
        // transform 不影響 layout：iframe 的 layout box 仍是未縮放的 previewWidth x frameHeight，
        // 會溢出這層 wrapper。外層若是 overflow: auto（素材詳情頁）就會多出一大塊可捲動的空白，
        // 因此這層一定要 overflow: hidden，讓 wrapper 的尺寸成為外層唯一看得到的尺寸。
        <div
          style={{
            width: previewWidth * scale,
            height: frameHeight * scale,
            overflow: 'hidden'
          }}
        >
          <iframe
            title='rich-media-preview'
            srcDoc={srcDoc}
            onLoad={handleIframeLoad}
            sandbox='allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox'
            style={{
              width: previewWidth,
              height: frameHeight,
              border: 'none',
              display: 'block',
              transform: `scale(${scale})`,
              transformOrigin: 'top left'
            }}
          />
        </div>
      )}
    </div>
  );
};
